import React, { CSSProperties, useEffect, useRef, useState } from 'react';

import moment from 'moment';
import { useDispatch } from 'react-redux';
import { useTranslation } from 'react-i18next';
import classNames from 'classnames';

import {
  useDeleteMessageMutation,
  useLikeMessageMutation,
  useDeleteLikeMutation,
} from '@school_ify/schoolify-repository/src/shared/types';

import useMessages from 'application/chat/messages';
import { ReactComponent as PinIcon } from 'assets/icons/pinn.svg';
import { ReactComponent as UnpinIcon } from 'assets/icons/unpin.svg';
import { ReactComponent as LikeIcon } from 'assets/icons/like.svg';
import { ReactComponent as CommentIcon } from 'assets/icons/comment.svg';
import { ReactComponent as DeleteIcon } from 'assets/icons/delete.svg';
import { ReactComponent as EditIcon } from 'assets/icons/edit.svg';
import { getUser } from 'redux/authentication/auth.slice';
import { getActiveChat, getRespondingMessage, getSocketUpdate } from 'redux/chat/selectors';
import {
  fetchAllMessages,
  togglePinMessage,
  selectPinToggled,
  selectRespondingClicked,
  setRespondingClicked,
} from 'redux/chat/specific_chat.slices';
import { useAppSelector } from 'redux/hooks';
import './Messages.scoped.scss';
import {
  addLike,
  deleteLike,
  deleteMessage,
  setReplys,
  setRespondingMessage,
} from 'redux/chat/actions';
import { changeCurrentScreen } from 'redux/mobile_screen_flow/mobile_screen_flow.slice';
import ThumbsUp from 'components/Icons/ThumbsUp';
import 'emoji-mart/css/emoji-mart.css';
import DeleteModal from 'components/DeleteModal/DeleteModal';
import ProfilePicture from 'clean/components/common/ProfilePicture/ProfilePicture';

import EditMessageForm from '../editMessageForm/EditMessageForm';

interface Props {
  children?: React.ReactNode;
  style?: CSSProperties;
  openModal?: boolean;
  handleModal?: () => void;
  responding?: object;
  setIsOnEdit?: React.Dispatch<React.SetStateAction<boolean>>;
  isOnEdit?: boolean;
  messages?: {
    body: string;
    user_id: string;
    is_pinned: boolean;
    created_at: string;
    responding: any;
    replys: any;
  }[];
}

const Messages: React.FC<Props> = ({ style, messages, setIsOnEdit, isOnEdit }) => {
  const { t } = useTranslation();
  const isThrouSocket = useAppSelector(getSocketUpdate);
  const [activeMessage, setActiveMessage] = useState({
    body: '',
    chat_id: '',
    created_at: '',
    id: '',
    is_pinned: false,
    user: {
      first_name: '',
      last_name: '',
    },
    user_id: '',
  });
  const { getAllMessages, hasNext, queryMore, addNewMessages, setCurrentPage } = useMessages();

  const activeChat = useAppSelector(getActiveChat);
  const user = useAppSelector(getUser);
  const [, setWelcomeMessage] = useState('');
  const messagesEndRef = useRef(null);
  const messageContainer = useRef();
  const dispatch = useDispatch();
  const [buttonClicked, setButtonClicked] = useState(false);
  const pinToggled = useAppSelector(selectPinToggled);
  const [isEmojiOpen, setIsEmojiOpen] = useState(false);
  const [modal, toggleModal] = useState(false);
  const [responding, setResponding] = useState({ id: null });

  const [deleteMessageMutation] = useDeleteMessageMutation();
  const [likeMessageMutation] = useLikeMessageMutation();
  const [deleteLikeMutation] = useDeleteLikeMutation();
  const respondingMessage = useAppSelector(getRespondingMessage);
  const clickedRes = useAppSelector(selectRespondingClicked);

  const respondingRef = useRef(null);
  const [resClicked, setResClicked] = useState({ id: null });

  function checkIfPreviosDayWasTheSame(i) {
    let [currDate, prevDate] = Array(2).fill(new Date().toISOString().split('T')[0]);
    if (messages[i].created_at) currDate = messages[i].created_at.split('T')[0];
    if (i >= 1 && messages[i - 1].created_at)
      prevDate = i >= 1 && messages[i - 1].created_at.split('T')[0];
    return prevDate === currDate;
  }

  function setUpWeldomeMessage() {
    if (activeChat.participants.length > 2) {
      setWelcomeMessage(`Welcome to ${activeChat.title} chanel`);
    } else {
      setWelcomeMessage('');
    }
  }

  const scrollToBottom = () => {
    if (!isOnEdit) {
      messagesEndRef.current.scrollIntoView();
    }
  };

  const handleRespondingClicked = e => {
    dispatch(setRespondingClicked(e.responding));
    setResClicked(e.responding);
  };

  // remove in refactoring phase
  async function getPreviosMessages() {
    const message = await getAllMessages(activeChat.id);
    addNewMessages(message);
    scrollToBottom();
  }

  function getMessages() {
    dispatch(fetchAllMessages(activeChat.id));
  }

  useEffect(() => {
    scrollToBottom();
  }, [isThrouSocket, messages]);

  useEffect(() => {
    if (activeChat) {
      setUpWeldomeMessage();
      if (activeChat.id) {
        // remove getPreviosMessages() in refactoring phase
        getPreviosMessages();
        getMessages();
        setCurrentPage(1);
      }
    }
  }, [activeChat, pinToggled]);

  function watchForTopEnd(e) {
    if (!activeChat) return;
    const offset = 0;
    const inPositionToPaginate = e.target.scrollTop === offset;
    if (hasNext && inPositionToPaginate) {
      queryMore(activeChat.id);
    }
  }

  useEffect(() => {
    setResClicked(clickedRes);
  }, [clickedRes]);

  const handlePined = e => {
    const payload = {
      data: {
        is_pinned: !e.is_pinned,
        id: e.id,
      },
      chatId: e.chat_id,
    };
    dispatch(togglePinMessage(payload));
    setButtonClicked(!buttonClicked);
    setResClicked({ id: null });
  };
  const handleEmoji = emoji => {
    setActiveMessage(prev => {
      return {
        ...prev,
        body: activeMessage.body + emoji,
      };
    });
  };
  const handleDeleteMessage = () => {
    const id = responding?.id;
    deleteMessageMutation({ variables: { id } });
    dispatch(deleteMessage(id));
    toggleModal(!modal);
    setResClicked({ id: null });
  };

  const onEdit = e => {
    setActiveMessage(e);
    setResClicked({ id: null });
  };

  const handleRespond = e => {
    dispatch(setRespondingMessage(e));
    setResClicked({ id: null });
  };
  const openReplys = e => {
    dispatch(changeCurrentScreen('replys'));
    dispatch(setReplys(e.replys));
    setResClicked({ id: null });
  };

  const handleLike = async e => {
    // check if liked already
    const indexOfLike = e.likes.map(like => like.chat_like_user.id).indexOf(user.id);

    if (indexOfLike === -1) {
      const response = await likeMessageMutation({ variables: { chat_message_id: e.id } });
      dispatch(addLike(response.data.insert_chat_message_likes_one));
    } else {
      const like = e.likes[indexOfLike];
      await deleteLikeMutation({ variables: { id: like.id } });

      dispatch(deleteLike(like));
    }
    setResClicked({ id: null });
  };
  const Hover = ({ onHover, children }) => (
    <div className="hover">
      <div className="hover__no-hover">{children}</div>
      <div className="hover__hover">{onHover}</div>
    </div>
  );

  const noLikesAndReplies = e => {
    return e.likes.length <= 0 && e.replys.length <= 0;
  };
  const handleToggleModal = e => {
    toggleModal(!modal);
    setResponding(e);
  };

  const iconSize = {
    width: '20px',
    height: '20px',
  };

  const renderThread = e => {
    return (
      <button className="reply-btn" onClick={() => openReplys(e)}>
        {t('common:chat_thread_text')} ({e.replys.length})
      </button>
    );
  };

  const renderHover = e => {
    return (
      <>
        <Hover
          onHover={
            <div className="content-hover-container">
              <div className={e.is_pinned ? 'full_on_hover' : 'content'}>
                {e.is_pinned && (
                  <div className="created-by">
                    <PinIcon
                      {...iconSize}
                      onClick={() => handlePined(e)}
                      className={e.is_pinned ? 'pin-icon-active' : 'pin-icon'}
                      style={{ width: '15px' }}
                    />
                    <p>Pinned</p>
                  </div>
                )}
                <div className="message-body-wrapper">
                  <p>{e.body}</p>
                </div>
                {!noLikesAndReplies(e) && (
                  <div className="message-actions-container">
                    {e.likes.length > 0 && (
                      <div
                        className={classNames('like-btn', {
                          active:
                            e.likes.map(like => like.chat_like_user.id).indexOf(user.id) !== -1,
                        })}
                        onClick={() => handleLike(e)}
                      >
                        {e.likes.length > 0 && <span>{e.likes.length}</span>}{' '}
                        <ThumbsUp fontSize={15} />
                      </div>
                    )}

                    {e.replys.length > 0 && renderThread(e)}
                  </div>
                )}
              </div>
              <div className="message-function">
                <div className="element-function">
                  {e.is_pinned ? (
                    <>
                      <UnpinIcon
                        {...iconSize}
                        onClick={() => handlePined(e)}
                        className="pin-icon-active"
                      />
                      <span className="tooltiptext">{t('chat:message_Tooltip.unpin')}</span>
                    </>
                  ) : (
                    <>
                      <PinIcon {...iconSize} onClick={() => handlePined(e)} className="pin-icon" />
                      <span className="tooltiptext">{t('chat:message_Tooltip.pin')}</span>
                    </>
                  )}
                </div>
                <div className="element-function">
                  <CommentIcon
                    {...iconSize}
                    onClick={() => handleRespond(e)}
                    className={e.is_pinned ? 'pin-icon-active' : 'pin-icon'}
                  />
                  <span className="tooltiptext">{t('chat:message_Tooltip.reply')}</span>
                </div>
                <div className="element-function">
                  <LikeIcon
                    {...iconSize}
                    onClick={() => handleLike(e)}
                    className={e.is_pinned ? 'pin-icon-active' : 'pin-icon'}
                  />
                  <span className="tooltiptext">{t('chat:message_Tooltip.like')}</span>
                </div>
                {e.user_id === user.id && (
                  <>
                    <div className="element-function">
                      <EditIcon
                        {...iconSize}
                        onClick={() => onEdit(e)}
                        className={e.is_pinned ? 'pin-icon-active' : 'pin-icon'}
                      />
                      <span className="tooltiptext">{t('chat:message_Tooltip.edit')}</span>
                    </div>
                    <div className="element-function">
                      <DeleteIcon
                        {...iconSize}
                        onClick={() => handleToggleModal(e)}
                        className={e.is_pinned ? 'pin-icon-active' : 'pin-icon'}
                      />
                      <span className="tooltiptext">{t('chat:message_Tooltip.delete')}</span>
                    </div>
                  </>
                )}
              </div>
            </div>
          }
        >
          <div
            className={classNames({
              'ref-res': e?.id === resClicked?.id,
              'content-pin-active': e.is_pinned,
              'responding-message':
                e.id === respondingMessage.id && respondingMessage.id !== undefined,
              content: true,
            })}
          >
            {e.is_pinned && (
              <div>
                <div className="created-by">
                  <PinIcon
                    {...iconSize}
                    onClick={() => handlePined(e)}
                    className={e.is_pinned ? 'pin-icon-active' : 'pin-icon'}
                    style={{ width: '15px' }}
                  />
                  <p>Pinned</p>
                </div>
              </div>
            )}
            <div className="message-body-wrapper">
              <p ref={e?.id === resClicked?.id ? respondingRef : null}>{e.body}</p>
            </div>
            {!noLikesAndReplies(e) && (
              <div className="message-actions-container">
                {e.likes.length > 0 && (
                  <div
                    className={classNames('like-btn', {
                      active: e.likes.map(like => like.chat_like_user.id).indexOf(user.id) !== -1,
                    })}
                    onClick={() => handleLike(e)}
                  >
                    {e.likes.length > 0 && <span>{e.likes.length}</span>} <ThumbsUp fontSize={15} />
                  </div>
                )}

                {e.replys.length > 0 && renderThread(e)}
              </div>
            )}
          </div>

          <div className="white-space" />
        </Hover>

        {e.id === activeMessage.id && (
          <EditMessageForm
            isEmojiOpen={isEmojiOpen}
            setIsEmojiOpen={setIsEmojiOpen}
            handleEmoji={handleEmoji}
            setActiveMessage={setActiveMessage}
            activeMessage={activeMessage}
            value={activeMessage.body}
            setIsOnEdit={setIsOnEdit}
            setResClicked={setResClicked}
          />
        )}
      </>
    );
  };

  const shouldGroupMessages = i => {
    const user_id = messages[i].user_id;
    const shouldGroupMessages = i > 0 && user_id === messages[i - 1].user_id;

    return !checkIfPreviosDayWasTheSame(i) || !shouldGroupMessages;
  };

  useEffect(() => {
    if (resClicked) {
      respondingRef.current?.scrollIntoView({
        behavior: 'smooth',
        block: 'center',
        inline: 'center',
      });
    }
  }, [resClicked]);

  const renderGroupChat = (e, i, time) => {
    return (
      <div key={`${e.user_id}-${i}`}>
        {!checkIfPreviosDayWasTheSame(i) && (
          <p className="message-date">
            <span>{moment(e.created_at).format('DD MMMM YYYY')}</span>
          </p>
        )}

        {e.responding && (
          <div className="responding-container">
            <span className="responding">
              <span className="avatar">
                <ProfilePicture userId={e.user_id} />
              </span>
              <span className="text">
                <span className="username">
                  <strong>{`${e.responding.user.first_name} ${e.responding.user.last_name}`}</strong>
                </span>
                <span className="pointer" onClick={() => handleRespondingClicked(e)}>
                  - {e.responding.body}
                </span>
              </span>
            </span>
          </div>
        )}

        {shouldGroupMessages(i) && (
          <div className="user-profile">
            <div className={`image-container ${e.user_id === user.id && 'me'}`}>
              <ProfilePicture userId={e.user_id} />
            </div>
            <strong>
              <p>
                {e.user?.first_name} {e.user?.last_name}
              </p>
            </strong>
            <p className="message-time">{time?.split(':').slice(0, 2).join(':')}</p>
          </div>
        )}
        {renderHover(e)}
      </div>
    );
  };

  const renderPrivateChat = (e, i, time) => {
    return (
      activeChat &&
      user && (
        <div key={`${e.user_id}-${i}`}>
          {!checkIfPreviosDayWasTheSame(i) && (
            <p className="message-date">
              <span>{moment(e.created_at).format('DD MMMM YYYY')}</span>
            </p>
          )}
          {e.responding && (
            <div className="responding-container">
              <span className="responding">
                <span className="avatar">
                  <ProfilePicture userId={e.responding.user.id} />
                </span>
                <span className="text">
                  <span className="username">
                    <strong>{`${e.responding.user.first_name} ${e.responding.user.last_name}`}</strong>
                  </span>
                  <span className="pointer" onClick={() => handleRespondingClicked(e)}>
                    - {e.responding.body}
                  </span>
                </span>
              </span>
            </div>
          )}
          {shouldGroupMessages(i) && (
            <div className="user-profile">
              <div className={`image-container ${e.user_id === user.id && 'me'}`}>
                <div className="avatar">
                  <ProfilePicture userId={e.user_id} />
                </div>
              </div>
              <p>
                <strong>
                  {e.user_id === user.id
                    ? `${user.first_name} ${user.last_name}`
                    : `${activeChat.title}`}
                </strong>
              </p>
              <p className="message-time">{time?.split(':').slice(0, 2).join(':')}</p>
            </div>
          )}
          {renderHover(e)}
        </div>
      )
    );
  };

  return (
    <div className="message-container" style={style} onScroll={watchForTopEnd}>
      <div ref={messageContainer} />
      <DeleteModal
        openModal={modal}
        handleModal={() => {
          toggleModal(!modal);
        }}
        responding={responding}
        handleCloseModal={() => {
          toggleModal(!modal);
        }}
        handleDelete={handleDeleteMessage}
        shouldDisplayMessage={true}
      />
      {messages &&
        messages.map((e, i) => {
          const time = e?.created_at?.split('T')[1];
          return activeChat && user && !activeChat.user && activeChat.participants.length > 0
            ? renderGroupChat(e, i, time)
            : renderPrivateChat(e, i, time);
        })}
      <div ref={messagesEndRef} />
    </div>
  );
};
export default Messages;
